import { ISubscriptionCallbackParams, TSubscriptionCallbackParams } from "@/Common/api/types/SubscriptionCallbackParams";
import { IPayPeriodSideBarCommissionCycle } from "@/Washouts/types/PayrollClosing/PayrollClosingSideBar";
import {
	IAdjustment,
	IOnSubmitPayrollToHR,
	IOnUpdatePayrollSubmitStatus,
	IUpdatePayrollSubmitStatus,
	ISubmitPayrollToHR,
	IAdjustmentItem,
	IOnApproveAllWashouts,
	IOnRetractAllWashouts,
} from "@/Washouts/types/PayrollClosing/subscriptionCallbacks";
import { IWashoutData } from "@/Washouts/types/WashoutReview";
import cloneDeep from "lodash-es/cloneDeep";

const onApproveAllWashoutsCallback = ({
	queryClient,
	response,
}: ISubscriptionCallbackParams<"onApproveAllWashouts", IOnApproveAllWashouts>) => {
	if (!response?.data?.onApproveAllWashouts) {
		return;
	}

	const data = response.data.onApproveAllWashouts;
	const locationId = data?.locationId;
	const payPeriodId = data?.payPeriodId;
	const commissionCycleId = data?.commissionCycleId;

	queryClient.setQueryData(["commissionCycles", locationId], (oldData: IPayPeriodSideBarCommissionCycle[]) => {
		return oldData.map((commissionCycle) => {
			if (commissionCycle._id === commissionCycleId) {
				const payPeriods = commissionCycle.payPeriods.map((payPeriod) => {
					if (payPeriod._id === payPeriodId) {
						const positionStatus = payPeriod.positionStatus.map((status) => {
							return data.positionStatus.find((entry) => entry.position === status.position) ?? status;
						});
						return {
							...payPeriod,
							positionStatus,
						};
					}
					return payPeriod;
				});
				return {
					...commissionCycle,
					payPeriods,
				};
			}
			return commissionCycle;
		});
	});

	data.positionStatus.forEach(({ position, status }) => {
		queryClient.setQueriesData<IWashoutData>({ queryKey: ["getWashout", payPeriodId, position], exact: false }, (oldData) => {
			const newData = cloneDeep(oldData);
			if (newData?.employee?.position === position && newData?.commissionCycleId === commissionCycleId) {
				return {
					...newData,
					review: {
						status, // Add the status field here
						statusSetBy: { fullName: data.statusSetBy },
						statusSetOn: data.statusSetOn,
					},
				};
			}
			return newData;
		});
	});

	queryClient.invalidateQueries({
		queryKey: ["washoutReview", "tree", locationId, payPeriodId],
		exact: true,
		refetchType: "active",
	});

	queryClient.invalidateQueries({
		queryKey: ["washoutApprovedByPosition", locationId, payPeriodId],
	});
};

const onRetractAllWashoutsCallback = ({
	queryClient,
	response,
}: ISubscriptionCallbackParams<"onRetractAllWashouts", IOnRetractAllWashouts>) => {
	if (!response?.data?.onRetractAllWashouts) {
		return;
	}

	const data = response.data.onRetractAllWashouts;
	const locationId = data?.locationId;
	const payPeriodId = data?.payPeriodId;
	const commissionCycleId = data?.commissionCycleId;

	queryClient.setQueryData(["commissionCycles", locationId], (oldData: IPayPeriodSideBarCommissionCycle[]) => {
		return oldData.map((commissionCycle) => {
			if (commissionCycle._id === commissionCycleId) {
				const payPeriods = commissionCycle.payPeriods.map((payPeriod) => {
					if (payPeriod._id === payPeriodId) {
						const positionStatus = payPeriod.positionStatus.map((status) => {
							return data.positionStatus.find((entry) => entry.position === status.position) ?? status;
						});
						return {
							...payPeriod,
							positionStatus,
						};
					}
					return payPeriod;
				});
				return {
					...commissionCycle,
					payPeriods,
				};
			}
			return commissionCycle;
		});
	});

	data.positionStatus.forEach(({ position, status }) => {
		queryClient.setQueriesData<IWashoutData>({ queryKey: ["getWashout", payPeriodId, position], exact: false }, (oldData) => {
			const newData = cloneDeep(oldData);
			if (newData?.employee?.position === position && newData?.commissionCycleId === commissionCycleId) {
				return {
					...newData,
					review: {
						status, // Add the status field here
						statusSetBy: { fullName: data.statusSetBy },
						statusSetOn: data.statusSetOn,
					},
				};
			}
			return newData;
		});
	});

	queryClient.invalidateQueries({
		queryKey: ["washoutReview", "tree", locationId, payPeriodId],
		exact: true,
		refetchType: "active",
	});

	queryClient.invalidateQueries({
		queryKey: ["washoutApprovedByPosition", locationId, payPeriodId],
	});
};
const onSubmitPayrollToHRCallback = ({
	queryClient,
	response,
}: ISubscriptionCallbackParams<"onSubmitPayrollToHR", IOnSubmitPayrollToHR>) => {
	if (!response.data?.onSubmitPayrollToHR) {
		return;
	}
	const { locationId, payPeriodId, updated } = response.data.onSubmitPayrollToHR;

	// add submission to table
	queryClient.setQueryData(["payrollClosing", "submissionHistory", locationId, payPeriodId], (oldData: ISubmitPayrollToHR[] = []) => {
		const newData = [...oldData];
		updated.forEach((newRow) => {
			newData.push({
				_id: newRow._id,
				submitter: newRow.submitter,
				statusDate: newRow.statusDate,
				status: newRow.status,
				position: newRow.position,
				dateRange: newRow.dateRange,
				companyId: newRow.companyId,
				displayId: newRow.displayId,
				providerPayPeriodId: newRow.providerPayPeriodId,
			});
		});
		return newData;
	});
};

const onUpdatePayrollSubmitStatusCallback = ({
	queryClient,
	response,
}: ISubscriptionCallbackParams<"onUpdatePayrollSubmitStatus", IOnUpdatePayrollSubmitStatus>) => {
	if (!response.data?.onUpdatePayrollSubmitStatus) {
		return;
	}
	const { locationId, payPeriodId, updated } = response.data.onUpdatePayrollSubmitStatus;
	queryClient.setQueryData(["payrollClosing", "submissionHistory", locationId, payPeriodId], (oldData: IUpdatePayrollSubmitStatus[]) => {
		return oldData?.map((row) => {
			if (row._id !== updated.id) {
				return row;
			}
			return {
				...row,
				status: updated.status,
				errors: updated.errors,
			};
		});
	});
};

const onCreateAdjustmentCallback = ({ queryClient, response }: TSubscriptionCallbackParams<"onCreateAdjustment", IAdjustment>) => {
	if (!response.data?.onCreateAdjustment) {
		return;
	}
	const { locationId, payPeriodId, adjustments } = response?.data?.onCreateAdjustment ?? {};
	queryClient.setQueryData(["payrollClosing", "adjustments", locationId, payPeriodId], (oldData: IAdjustmentItem[]) => {
		const data = [...oldData];
		const find = data.find((element) => element._id === adjustments[0]._id);
		if (!find) {
			data.push(adjustments[0]);
		}
		return data;
	});
};

const onDeleteAdjustmentCallback = ({ queryClient, response }: TSubscriptionCallbackParams<"onDeleteAdjustment", IAdjustment>) => {
	if (!response.data?.onDeleteAdjustment) {
		return;
	}
	const { locationId, payPeriodId, adjustments } = response.data?.onDeleteAdjustment ?? {};
	if (adjustments) {
		queryClient.setQueryData(["payrollClosing", "adjustments", locationId, payPeriodId], (oldData: IAdjustmentItem[]) => {
			return oldData.filter((item) => !adjustments.some((element) => element._id === item._id));
		});
	}
};

const onUpdateAdjustmentCallback = ({ queryClient, response }: TSubscriptionCallbackParams<"onUpdateAdjustment", IAdjustment>) => {
	if (!response.data?.onUpdateAdjustment) {
		return;
	}
	const { locationId, payPeriodId, adjustments } = response.data?.onUpdateAdjustment ?? {};
	if (adjustments?.length) {
		queryClient.setQueryData(["payrollClosing", "adjustments", locationId, payPeriodId], (oldData: IAdjustmentItem[]) => {
			const data = [...oldData];
			return data.map((item) => {
				const adjustment = adjustments.find((element) => element._id === item._id);
				return adjustment ?? item;
			});
		});
	}
};

export {
	onApproveAllWashoutsCallback,
	onRetractAllWashoutsCallback,
	onSubmitPayrollToHRCallback,
	onUpdatePayrollSubmitStatusCallback,
	onCreateAdjustmentCallback,
	onDeleteAdjustmentCallback,
	onUpdateAdjustmentCallback,
};
